import {Injectable} from '@angular/core';
import {environment} from '../../../../environments/environment';
import {HttpClient} from '@angular/common/http';
import {AuthService} from '../../auth.service';
import {ApiMethod} from '../../../decorators/api-method.decorator';
import {Observable} from 'rxjs';
import {CompleteRequest, CompletePreApprovalRequest} from '../../../models/completeRequest';
import {JobProcedureFormBypassRequestDetails} from '../../../models/jobProcedureFormBypassRequestDetails';
import { ProcedurePreApprovalRequestDetails } from 'src/app/models/procedurePreApprovalRequestDetails.model';
import { JobProcedureCopilotConfirmationDetails, JobProcedureFormCopilotConfirmation, JobProcedureFormCopilotConfirmationDetails, JobProcedureFormPhoto, JobProcedureFormResult } from 'src/app/models/procedure-copilot-confirmation-details.model';
import { JobProcedureFormValidationsSkipRequestDetails } from '../../../models/jobProcedureFormValidationsSkipRequestDetails';
import { JobProcedureSummaryReview } from 'src/app/models/job-procedure-summary-review.model';

@Injectable({
	providedIn: 'root'
})
export class JobProcedureService{
	private baseJobUrl = `${environment.baseUrl}/jobProcedure`;
	constructor(
		private httpClient: HttpClient,
		private authService: AuthService
	) {}

	@ApiMethod()
	getJobBypassRequiredRequest(token: string): Observable<JobProcedureFormBypassRequestDetails>{
		return this.httpClient.get<JobProcedureFormBypassRequestDetails>(`${this.baseJobUrl}/${token}/bypass-request-details`, { headers: this.authService.getAuthenticationHeader() });
	}

	@ApiMethod()
	updateJobProcedureFormBypassRequest(token: string, bypassRequest: CompleteRequest): Observable<void>{
		return this.httpClient.patch<void>(`${this.baseJobUrl}/${token}/bypass-request`, bypassRequest, { headers: this.authService.getAuthenticationHeader() });
	}

	@ApiMethod()
	getJobValidationsSkipRequiredRequest(token: string): Observable<JobProcedureFormValidationsSkipRequestDetails>{
		return this.httpClient.get<JobProcedureFormValidationsSkipRequestDetails>(`${this.baseJobUrl}/${token}/validations-skip-request-details`, { headers: this.authService.getAuthenticationHeader() });
	}

	@ApiMethod()
	updateJobProcedureFormValidationsSkipRequest(token: string, validationsSkipRequest: CompleteRequest): Observable<void>{
		return this.httpClient.patch<void>(`${this.baseJobUrl}/${token}/validations-skip-request`, validationsSkipRequest, { headers: this.authService.getAuthenticationHeader() });
	}

	@ApiMethod()
	getProcedurePreApprovalRequest(token: string): Observable<ProcedurePreApprovalRequestDetails>{
		return this.httpClient.get<ProcedurePreApprovalRequestDetails>(`${this.baseJobUrl}/${token}/pre-approval-request-details`, { headers: this.authService.getAuthenticationHeader() });
	}

	@ApiMethod()
	updateProcedurePreApprovalRequest(token: string, preApprovalRequest: CompletePreApprovalRequest): Observable<void>{
		return this.httpClient.patch<void>(`${this.baseJobUrl}/${token}/pre-approval-request`, preApprovalRequest, { headers: this.authService.getAuthenticationHeader() });
	}

	@ApiMethod()
	refreshCopilotConfirmationLink(token: string): Observable<void>{
		return this.httpClient.post<void>(`${this.baseJobUrl}/${token}/copilot-confirmation/refresh-link`, {});
	}

	@ApiMethod()
	getProcedureCopilotConfirmationDetails(token: string): Observable<JobProcedureCopilotConfirmationDetails>{
		return this.httpClient.get<JobProcedureCopilotConfirmationDetails>(`${this.baseJobUrl}/${token}/copilot-confirmation-details`);
	}

	@ApiMethod()
	getOtherProcedureCopilotConfirmationDetails(token: string): Observable<JobProcedureCopilotConfirmationDetails[]>{
		return this.httpClient.get<JobProcedureCopilotConfirmationDetails[]>(`${this.baseJobUrl}/${token}/copilot-confirmation-procedures`);
	}

	@ApiMethod()
	isJobProcedureFormConfirmed(token: string, jobProcedureFormId: number): Observable<boolean>{
		return this.httpClient.get<boolean>(`${this.baseJobUrl}/${token}/copilot-confirmation-details`);
	}

	@ApiMethod()
	updateCopilotStep(token: string, step: JobProcedureFormCopilotConfirmation): Observable<JobProcedureFormCopilotConfirmationDetails>{
		return this.httpClient.post<JobProcedureFormCopilotConfirmationDetails>(`${this.baseJobUrl}/${token}/copilot-confirmation/steps/${step.jobProcedureFormId}/update`, step);
	}

	@ApiMethod()
	stepPhotos(token: string, step: JobProcedureFormCopilotConfirmationDetails): Observable<JobProcedureFormPhoto[]>{
		return this.httpClient.get<JobProcedureFormPhoto[]>(`${this.baseJobUrl}/${token}/copilot-confirmation/steps/${step.jobProcedureFormId}/photos`);
	}

	@ApiMethod()
	stepPhotosV2(token: string, jobProcedureFormId: number): Observable<JobProcedureFormPhoto[]>{
		return this.httpClient.get<JobProcedureFormPhoto[]>(`${this.baseJobUrl}/${token}/copilot-confirmation/steps/${jobProcedureFormId}/photos`);
	}

	@ApiMethod()
	stepPhotosV3(token: string, jobProcedureFormId: number): Observable<JobProcedureFormPhoto[]>{
		return this.httpClient.get<JobProcedureFormPhoto[]>(`${this.baseJobUrl}/${token}/summary-review/steps/${jobProcedureFormId}/photos`);
	}

	@ApiMethod()
	stepResult(token: string, step: JobProcedureFormCopilotConfirmationDetails): Observable<JobProcedureFormResult>{
		return this.httpClient.get<JobProcedureFormResult>(`${this.baseJobUrl}/${token}/copilot-confirmation/steps/${step.jobProcedureFormId}/result`);
	}

	@ApiMethod()
	stepResultV2(token: string, jobProcedureFormId: number): Observable<JobProcedureFormResult>{
		return this.httpClient.get<JobProcedureFormResult>(`${this.baseJobUrl}/${token}/copilot-confirmation/steps/${jobProcedureFormId}/result`);
	}

	@ApiMethod()
	stepResultV3(token: string, jobProcedureFormId: number): Observable<JobProcedureFormResult>{
		return this.httpClient.get<JobProcedureFormResult>(`${this.baseJobUrl}/${token}/summary-review/steps/${jobProcedureFormId}/result`);
	}

	@ApiMethod()
	getCopilotConfirmationHistory(token: string, jobProcedureFormId: number): Observable<JobProcedureFormCopilotConfirmation[]>{
		return this.httpClient.get<JobProcedureFormCopilotConfirmation[]>(`${this.baseJobUrl}/${token}/copilot-confirmation/steps/${jobProcedureFormId}/history`);
	}

	@ApiMethod()
	getProcedureSummaryReviewDetails(token: string): Observable<JobProcedureSummaryReview>{
		return this.httpClient.get<JobProcedureSummaryReview>(`${this.baseJobUrl}/${token}/summary-review-details`);
	}

	@ApiMethod()
	getSummaryConfirmationHistoryByJobProcedureFormId(token: string, jobProcedureFormId: number): Observable<JobProcedureFormCopilotConfirmation[]>{
		return this.httpClient.get<JobProcedureFormCopilotConfirmation[]>(`${this.baseJobUrl}/${token}/summary-review/step/${jobProcedureFormId}/history`);
	}

	@ApiMethod()
	generateProcedureSummaryReport(token: string, jobId: number): Observable<void>{
		return this.httpClient.post<void>(`${this.baseJobUrl}/${token}/${jobId}/summary-report`, {});
	}

	@ApiMethod()
	refreshProcedureSummaryReviewLink(token: string): Observable<void>{
		return this.httpClient.post<void>(`${this.baseJobUrl}/${token}/summary-review/refresh-link`, {});
	}
}
